import { __awaiter, __generator } from "tslib";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Texture as BJSTexture } from "@babylonjs/core/Materials/Textures/texture";
import { getDataPath, Log } from "../../../utils";
import { ERR_INVALID_ARGUMENT } from "../../../utils/error";
import { isNotNullOrEmpty, joinPath } from "../../../utils/string";
import { FileNotFoundError } from "../../io/Types";
import { deserializePbrMaterial, serializePbrMaterial } from "./PbrMaterialSerialization";
/** @internal */
export function serializeMaterials(pManager, pContext) {
    if (pContext == null || pContext.materialIds == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    var tIOMaterials = new Array();
    for (var _i = 0, _a = pContext.materialIds; _i < _a.length; _i++) {
        var tMatId = _a[_i];
        var tMat = pManager.getMaterial(tMatId);
        if (tMat != null) {
            var tIOMat = serializePbrMaterial(pContext, tMat);
            tIOMaterials.push(tIOMat);
        }
        else {
            Log.warn("Unknown material id: " + tMatId);
            tIOMaterials.push({ id: tMatId });
        }
    }
    return (tIOMaterials);
}
/** @internal */
export function deserializeMaterials(pManager, pMaterials, pContext) {
    if (pMaterials == null || pContext == null)
        return;
    for (var _i = 0, pMaterials_1 = pMaterials; _i < pMaterials_1.length; _i++) {
        var tIOMat = pMaterials_1[_i];
        var tMat = deserializePbrMaterial(tIOMat, pContext, pManager);
        if (tMat != null)
            pManager.addMaterial(tIOMat.id, tMat);
    }
}
/** @internal */
export function serializeTextures(pManager, pContext) {
    if (pContext == null || pContext.textureIds == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    var tIOTextures = new Array();
    for (var _i = 0, _a = pContext.textureIds; _i < _a.length; _i++) {
        var tTextureId = _a[_i];
        var tTexture = pManager.getTexture(tTextureId);
        if (tTexture != null) {
            var tResTex = pManager._getResourceTextures().get(tTextureId);
            if (tResTex != null && tTexture.isResource) {
                var tIOTexture = {};
                //id
                tIOTexture.id = tTextureId;
                //type
                if (tResTex.type !== "jpg")
                    tIOTexture.type = tResTex.type;
                //name
                if (tTexture.name != tTextureId && isNotNullOrEmpty(tTexture.name))
                    tIOTexture.name = tTexture.name;
                //transparent
                if (tTexture.hasAlpha)
                    tIOTexture.transparent = true;
                tIOTextures.push(tIOTexture);
            }
        }
        else {
            Log.warn("Unknown texture id: " + tTextureId);
            tIOTextures.push({ id: tTextureId });
        }
    }
    return (tIOTextures);
}
/** @internal */
export function addTexturesToContainer(pManager, pTextureIds, pContainer, pProgressCallback) {
    return __awaiter(this, void 0, void 0, function () {
        var tCount, i;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (pTextureIds == null || pContainer == null)
                        throw new Error(ERR_INVALID_ARGUMENT);
                    tCount = pTextureIds.length;
                    i = 0;
                    _a.label = 1;
                case 1:
                    if (!(i < tCount)) return [3 /*break*/, 4];
                    return [4 /*yield*/, addTextureToContainer(pManager, pTextureIds[i], pContainer)];
                case 2:
                    _a.sent();
                    if (pProgressCallback != null)
                        pProgressCallback((i / tCount) * 100.0);
                    _a.label = 3;
                case 3:
                    ++i;
                    return [3 /*break*/, 1];
                case 4: return [2 /*return*/];
            }
        });
    });
}
/** @internal */
export function readTexturesFromContainer(pManager, pTextures, pContainer, pProgressCallback) {
    return __awaiter(this, void 0, void 0, function () {
        var tCount, i;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (pTextures == null || pContainer == null)
                        throw new Error(ERR_INVALID_ARGUMENT);
                    tCount = pTextures.length;
                    i = 0;
                    _a.label = 1;
                case 1:
                    if (!(i < tCount)) return [3 /*break*/, 4];
                    return [4 /*yield*/, readTextureFromContainer(pManager, pTextures[i], pContainer)];
                case 2:
                    _a.sent();
                    if (pProgressCallback != null)
                        pProgressCallback((i / tCount) * 100.0);
                    _a.label = 3;
                case 3:
                    ++i;
                    return [3 /*break*/, 1];
                case 4: return [2 /*return*/];
            }
        });
    });
}
//// internals /////////////////////////////////////////////////////////////////////////////////////
function addTextureToContainer(pManager, pTextId, pContainer) {
    var tResTex = pManager._getResourceTextures().get(pTextId);
    var tTexture = pManager.getTexture(pTextId);
    if (tResTex == null || tTexture == null) {
        Log.warn("Unknown texture id: " + pTextId);
        return (Promise.resolve());
    }
    if (tResTex.data == null) {
        Log.warn("Missing texture data for id: " + pTextId);
        return (Promise.resolve());
    }
    //do not embed fallback textures
    if (tTexture.isFallback)
        return (Promise.resolve());
    var tFilename = "textures/" + pTextId + "." + tResTex.type;
    //try use texture from cache first
    //no compression for textures, jpg and png are already compressed
    var tPromise = pContainer.addFileFromCache(tFilename, false).then(function (pAdded) {
        if (pAdded)
            return (Promise.resolve());
        else
            return (pContainer.addFile(tResTex.data, tFilename, false, true));
    }).catch(function (pError) {
        Log.error("Failed to embed texture " + pTextId, pError);
    });
    return (tPromise);
}
function readTextureFromContainer(pManager, pTex, pContainer) {
    if (pTex == null || pManager.hasTexture(pTex.id))
        return (Promise.resolve());
    var tType = "jpg";
    if (pTex.type != null)
        tType = pTex.type;
    var tHasAlpha = pTex.transparent != null && pTex.transparent;
    var tPromise = pContainer.getFile("textures/" + pTex.id + "." + tType, true).then(function (pBuffer) {
        var tTexture = pManager._internalCreateResourceTexture(pTex.id, pTex.name, tType, pBuffer, tHasAlpha);
        if (tTexture == null) {
            Log.error("Failed to load texture " + pTex.id);
        }
    }).catch(function (pError) {
        //use fallback texture
        createFallbackTexture(pManager, pTex.id, pTex.name, tType, tHasAlpha);
        //register as missing texture
        pManager.missingResourceTextures.add(pTex.id);
        if (!(pError instanceof FileNotFoundError))
            Log.error("Failed to get embedded texture " + pTex.id, pError);
    });
    return (tPromise);
}
function createFallbackTexture(pManager, pId, pName, pType, pHasAlpha) {
    //always create as RGBA beause it may be reused for both cases
    var tTexture = pManager._internalCreateTexture(pId, pName, joinPath(getDataPath(), "core/textures/fallback.png"), true, Engine.TEXTUREFORMAT_RGBA, BJSTexture.NEAREST_SAMPLINGMODE);
    tTexture.isFallback = true;
    tTexture.hasAlpha = pHasAlpha;
    var tResTex = {
        data: null,
        blobUrl: null,
        type: pType
    };
    pManager._getResourceTextures().set(pId, tResTex);
    return (tTexture);
}
